import { ExpirationStorageManager, StorageManagerDriver } from '@kc-console/core';

import { svgToDataUrl } from './svgToDataUrl';

import type { DataUrl } from '../types';

const SVG_CACHE_STORAGE_KEY = 'kc-svgCache';
const SVG_CACHE_DATA_KEY = 'svgs';
const CACHE_EXPIRE_TIME_SECONDS = 30 * 60; // 30분

type SvgCacheMap = {
  [url: string]: string; // SVG 텍스트만 저장
};

type SvgCacheStorage = {
  [SVG_CACHE_DATA_KEY]: SvgCacheMap;
};

type SvgMemoryCache = {
  dataUrl: DataUrl;
  svg: string;
};

/**
 * SVG 캐싱을 위한 하이브리드 캐시 매니저
 * @description
 * - 1차: 메모리 캐시 (Map) - 빠른 접근
 * - 2차: 로컬스토리지 - 새로고침 후에도 유지
 * - SVG 텍스트만 저장하고 Data URL은 런타임 변환
 */
class SvgCacheManager {
  // 메모리 캐시 (1차 캐시)
  private memoryCache = new Map<string, SvgMemoryCache>();

  // 로컬스토리지 매니저 (2차 캐시)
  private storageManager = new ExpirationStorageManager<SvgCacheStorage>(
    SVG_CACHE_STORAGE_KEY,
    StorageManagerDriver.LOCALSTORAGE,
  );

  // 진행 중인 fetch 요청을 추적 (중복 요청 방지)
  private pendingFetches = new Map<string, Promise<SvgMemoryCache>>();

  /**
   * URL로부터 SVG를 fetch
   */
  private async fetchSvg(url: string): Promise<string> {
    const response = await fetch(`${url}?cacheBust=${new Date().getTime()}`);
    const text = await response.text();

    // SVG 유효성 검사
    if (!text.includes('<svg')) {
      throw new Error('Invalid SVG content');
    }

    return text;
  }

  /**
   * url에 대한 svg + dataUrl 데이터 캐싱
   *
   * 1차: 메모리 캐시 탐색 -> 2차: 로컬스토리지 탐색 -> 3차: fetch (이후 메모리/로컬스토리지 저장)
   * @param url
   * @returns SvgMemoryCache = { svg: string; dataUrl: DataUrl; }
   */
  private async cacheSvg(url: string): Promise<SvgMemoryCache> {
    // 1차: 메모리 캐시 확인
    const cached = this.memoryCache.get(url);

    if (cached) {
      return cached;
    }

    // 진행 중인 fetch가 있는지 확인
    const pendingFetch = this.pendingFetches.get(url);
    if (pendingFetch) {
      return pendingFetch;
    }

    // 전체 로직을 Promise로 감싸서 pendingFetches에 먼저 등록
    const fetchPromise = (async () => {
      try {
        // 2차: 로컬스토리지 확인
        const storageData = await this.storageManager.get(SVG_CACHE_DATA_KEY);
        const storedSvg = storageData?.[url];

        if (storedSvg) {
          // 로컬스토리지에서 가져온 데이터를 메모리 캐시에도 저장
          const dataUrl = svgToDataUrl(storedSvg);
          const result = { svg: storedSvg, dataUrl };
          this.memoryCache.set(url, result);
          return result;
        }

        // 3차: fetch 후 양쪽(메모리 / 로컬스토리지) 캐시에 저장
        const svg = await this.fetchSvg(url);
        const dataUrl = svgToDataUrl(svg);

        // 메모리 캐시 저장
        this.memoryCache.set(url, { svg, dataUrl });

        // 로컬스토리지 저장 (SVG 텍스트만)
        await this.storageManager.set(
          SVG_CACHE_DATA_KEY,
          { ...storageData, [url]: svg },
          CACHE_EXPIRE_TIME_SECONDS,
        );

        return { svg, dataUrl };
      } finally {
        // fetch 완료 후 pendingFetches에서 제거
        this.pendingFetches.delete(url);
      }
    })();

    // 즉시 진행 중인 fetch로 등록 (로컬스토리지 확인 전에)
    this.pendingFetches.set(url, fetchPromise);

    return fetchPromise;
  }

  /**
   * SVG 텍스트 가져오기
   */
  async getSvg(url: string): Promise<string> {
    const { svg } = await this.cacheSvg(url);
    return svg;
  }

  /**
   * Data URL 가져오기
   */
  async getDataUrl(url: string): Promise<DataUrl> {
    const { dataUrl } = await this.cacheSvg(url);
    return dataUrl;
  }

  /**
   * 특정 URL의 캐시 삭제
   */
  async remove(url: string): Promise<void> {
    // 메모리 캐시 삭제
    this.memoryCache.delete(url);

    // 로컬스토리지 삭제
    const storageData = await this.storageManager.get(SVG_CACHE_DATA_KEY);
    if (storageData?.[url]) {
      const { [url]: _, ...rest } = storageData;
      await this.storageManager.set(SVG_CACHE_DATA_KEY, rest, CACHE_EXPIRE_TIME_SECONDS);
    }
  }

  /**
   * 전체 캐시 초기화
   */
  async clear(): Promise<void> {
    this.memoryCache.clear();
    await this.storageManager.set(SVG_CACHE_DATA_KEY, {}, CACHE_EXPIRE_TIME_SECONDS);
  }

  /**
   * 메모리 캐시 크기 확인 (디버깅용)
   */
  getMemoryCacheSize(): number {
    return this.memoryCache.size;
  }
}

// 싱글톤 인스턴스
const svgCacheManager = new SvgCacheManager();

export default svgCacheManager;
